import React from 'react'
import { Radio, Space } from 'antd'
import { CreditCardOutlined, BankOutlined } from '@ant-design/icons'
import { calcCardProcessingFee, calcCheckoutTotal } from '../../utils/publicPaymentFees'

export type PublicPaymentMethod = 'card' | 'ach'

export interface PublicPaymentMethodChooserProps {
  value: PublicPaymentMethod
  onChange: (method: PublicPaymentMethod) => void
  subtotal: number
  feePercent: number
  achEnabled: boolean
  cardOnly?: boolean
  variant?: 'boxed' | 'compact'
}

function radioStyle(
  variant: 'boxed' | 'compact',
  selected: boolean,
  isAchOption: boolean
): React.CSSProperties {
  if (variant !== 'boxed') {
    return { alignItems: 'flex-start', whiteSpace: 'normal' }
  }
  return {
    padding: 16,
    border: '1px solid #d9d9d9',
    borderRadius: 8,
    width: '100%',
    background: selected ? (isAchOption ? '#f6ffed' : '#f0f5ff') : 'transparent',
    alignItems: 'flex-start',
    whiteSpace: 'normal',
  }
}

const OptionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontWeight: 500 }}>{children}</div>
)

const OptionHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 12, color: '#666' }}>{children}</div>
)

export const PublicPaymentMethodChooser: React.FC<PublicPaymentMethodChooserProps> = ({
  value,
  onChange,
  subtotal,
  feePercent,
  achEnabled,
  cardOnly = false,
  variant = 'boxed',
}) => {
  const pct = feePercent > 0 ? feePercent : 0
  const cardFee = calcCardProcessingFee(subtotal, pct)
  const cardTotal = calcCheckoutTotal(subtotal, pct, 'card')
  const showAch = achEnabled && !cardOnly

  if (cardOnly || !showAch) {
    const inner = (
      <Space align="start">
        <CreditCardOutlined style={{ fontSize: 20, color: '#1890ff', marginTop: 2 }} />
        <span>
          <OptionTitle>Pay with credit or debit card</OptionTitle>
          <OptionHint>
            {pct > 0 ? (
              <>
                {pct}% processing fee (${cardFee.toFixed(2)}) • Total: ${cardTotal.toFixed(2)}
              </>
            ) : (
              <>Instant payment</>
            )}
          </OptionHint>
        </span>
      </Space>
    )
    if (variant === 'boxed') {
      return (
        <div
          style={{
            padding: 16,
            marginBottom: 24,
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            background: '#f0f5ff',
          }}
        >
          {inner}
        </div>
      )
    }
    return inner
  }

  return (
    <Radio.Group value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Radio value="card" style={radioStyle(variant, value === 'card', false)}>
          <Space align="start">
            <CreditCardOutlined style={{ fontSize: 20, color: '#1890ff', marginTop: 2 }} />
            <span>
              <OptionTitle>Credit or debit card</OptionTitle>
              <OptionHint>
                {pct > 0 ? (
                  <>
                    {pct}% processing fee (${cardFee.toFixed(2)}) • Total at checkout: $
                    {cardTotal.toFixed(2)}
                  </>
                ) : (
                  <>Instant payment; no surcharge configured.</>
                )}
              </OptionHint>
            </span>
          </Space>
        </Radio>
        <Radio value="ach" style={radioStyle(variant, value === 'ach', true)}>
          <Space align="start">
            <BankOutlined style={{ fontSize: 20, color: '#52c41a', marginTop: 2 }} />
            <span>
              <OptionTitle>Bank account (ACH)</OptionTitle>
              <OptionHint>
                No processing fee • Total at checkout: ${subtotal.toFixed(2)} • Typically 2–4
                business days
              </OptionHint>
            </span>
          </Space>
        </Radio>
      </Space>
    </Radio.Group>
  )
}
